import {ChangeDetectionStrategy, Component, Inject, OnInit, ViewEncapsulation} from "@angular/core";
import {Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {SESSION_STORAGE, StorageService} from "ngx-webstorage-service";
import {HttpEventService} from "src/app/services/http/http-event.service";
import OktaAuth from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import { combineLatest, firstValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { IvyMastheadHelpMenu, IvyMastheadMainMenu, IvyMastheadProfileMenu, IvyRouterLinkDescriptor, } from "@crs/ivy";

import { FileOperationsLogoutService } from "src/app/common/service/file-operations-logout.service";
import { UserService } from "src/app/services/user.service";
import { HeaderService } from "../../../services/header/header.service";
import { Client } from "../../../modal/organization/Client";
import {StoreManagerService} from "../../../services/storage.service";

@Component({
    selector: "header-component",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HeaderComponent implements OnInit {
	helpUrl: string;
	itemsArray: any = [];
	isContextSelected: boolean = this.storage.get("isContextSelected");

	/** Home Link */
	home$: Observable<IvyRouterLinkDescriptor> = this.translate
		.get("product.name_an")
		.pipe(
			map((homeLbl) => {
				if (this.userService.getUserType() == 'PO') {
					homeLbl = this.translate.instant("product.name_po")
				}
				homeLbl = homeLbl + "®".sup();
				return {
					text: homeLbl,
					ariaLabel: homeLbl,
					routerLink: [""],
				};
			})
		);

	/** Main Menu */
	mainMenu$: Observable<IvyMastheadMainMenu> = this.headerService.getMenuItems(this.storage.get('org'));

	/** Profile Menu */
	profileMenu$: Observable<IvyMastheadProfileMenu> = combineLatest([
		this.translate.get("masthead.profile"),
		this.translate.get("masthead.logoutMenu"),
		this.oktaAuth.getUser()
	]).pipe(
		map(([profileLbl, logoutLbl, userClaims]) => {
			return {
				ariaLabel: profileLbl,
				tooltip: profileLbl,
				logoutLabel: logoutLbl,
				user: {displayName: userClaims.name as string},
			};
		})
	);

	/** Help Menu */
	helpMenu$: Observable<IvyMastheadHelpMenu> = combineLatest([
		this.translate.get("masthead.help"),
		this.translate.get("masthead.about"),
	]).pipe(
		map(([help, about]) => {
			if (this.userService.getUserType() === "PO") {
				this.helpUrl = `${window.location.protocol}//${window.location.hostname}/help/po/index.html`;
			} else {
				this.helpUrl = `${window.location.protocol}//${window.location.hostname}/help/fn/index.html`;
			}
			return {
				ariaLabel: help,
				tooltip: help,
				items: [
					{text: about, ariaLabel: about, url: this.helpUrl, target: '_blank'},
				],
			};
		})
	);

	constructor(
		public router: Router,
		@Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
		private translate: TranslateService,
		public httpEventService: HttpEventService,
		private userService: UserService,
		private storeManagerService: StoreManagerService,
		@Inject(SESSION_STORAGE) private storage: StorageService,
		private readonly headerService: HeaderService,
		private readonly fileOperationsLogout: FileOperationsLogoutService
	) {}

	ngOnInit() {
		this.headerService.getMenuItems(this.storage.get("org"));
		this.storeManagerService.org$.subscribe((c: Client | null) => {
			this.storage.set("isContextSelected", !!c);
			this.isContextSelected = !!c;
		});
		this.storeManagerService.orgSubject.next(this.storage.get('org') ?? null);
	}

	async _onLogout(_e: Event): Promise<void> {
		await firstValueFrom(this.fileOperationsLogout.revokeTokenBeforeLogout()).catch(() => {});
		await this.oktaAuth.signOut();
		sessionStorage.clear();
	}

}


